import { BookOpen, CheckCircle2, Database, Search, Users, Wrench } from "lucide-react";
import type { ComponentType } from "react";

/**
 * Deck drawings ported to the site palette (`#2337` S2). Every icon is
 * decorative: `aria-hidden`, never focusable.
 */
export interface PitchIconProps {
  size?: number | undefined;
  className?: string | undefined;
}

const C = {
  teal: "pitch-icon-fill-teal",
  ink: "pitch-icon-fill-ink",
  soft: "pitch-icon-fill-soft",
  mint: "pitch-icon-fill-mint",
  accent: "pitch-icon-fill-accent",
  surface: "pitch-icon-fill-surface",
};

function Frame({ size = 64, className, children }: PitchIconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      {children}
    </svg>
  );
}

export function PlannerIcon(props: PitchIconProps) {
  return (
    <Frame {...props}>
      <rect x="12" y="14" width="40" height="28" rx="3" className={C.teal} />
      <rect x="16" y="18" width="32" height="20" rx="1" className={C.soft} />
      <rect x="19" y="22" width="12" height="4" rx="1" className={C.accent} />
      <path d="M6 45h52l-4 7H10z" className={C.ink} />
    </Frame>
  );
}

export function MemberIcon(props: PitchIconProps) {
  return (
    <Frame {...props}>
      <rect x="20" y="6" width="24" height="52" rx="5" className={C.teal} />
      <rect x="23" y="12" width="18" height="38" rx="2" className={C.soft} />
      <rect x="26" y="16" width="12" height="4" rx="1" className={C.accent} />
      <circle cx="32" cy="54" r="1.8" className={C.soft} />
    </Frame>
  );
}

export function ChatIcon(props: PitchIconProps) {
  return (
    <Frame {...props}>
      <path
        d="M8 10h48a4 4 0 0 1 4 4v26a4 4 0 0 1-4 4H28L14 56V44H8a4 4 0 0 1-4-4V14a4 4 0 0 1 4-4z"
        className={C.accent}
      />
      <circle cx="20" cy="27" r="3.5" className={C.surface} />
      <circle cx="32" cy="27" r="3.5" className={C.surface} />
      <circle cx="44" cy="27" r="3.5" className={C.surface} />
    </Frame>
  );
}

export function CoordinatorIcon(props: PitchIconProps) {
  return (
    <Frame {...props}>
      <path
        d="M12 36v-6a20 20 0 0 1 40 0v6"
        className="pitch-icon-stroke-teal"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <rect x="7" y="31" width="12" height="18" rx="5" className={C.ink} />
      <rect x="45" y="31" width="12" height="18" rx="5" className={C.ink} />
      <path
        d="M51 48c0 7-6 10-14 10"
        className="pitch-icon-stroke-teal"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <circle cx="34" cy="58" r="4" className={C.accent} />
    </Frame>
  );
}

export function ManagementIcon(props: PitchIconProps) {
  return (
    <Frame {...props}>
      <path
        d="M4 32c8-12 17-18 28-18s20 6 28 18c-8 12-17 18-28 18S12 44 4 32z"
        className={C.teal}
      />
      <circle cx="32" cy="32" r="10" className={C.surface} />
      <circle cx="32" cy="32" r="5" className={C.ink} />
    </Frame>
  );
}

/** A person in a cap with a small badge. Used for a field role. */
export function OfficerIcon(props: PitchIconProps) {
  return (
    <Frame {...props}>
      <path d="M10 64c0-13 10-21 22-21s22 8 22 21z" className={C.teal} />
      <circle cx="32" cy="30" r="10" className={C.mint} />
      <rect x="23" y="14" width="18" height="8" rx="3" className={C.accent} />
      <path d="M25 43l7 9 7-9z" className={C.accent} />
    </Frame>
  );
}

/** A person with a cap and a band. Used for a lead role. */
export function SupervisorIcon(props: PitchIconProps) {
  return (
    <Frame {...props}>
      <path d="M10 64c0-13 10-21 22-21s22 8 22 21z" className={C.ink} />
      <circle cx="32" cy="31" r="10" className={C.mint} />
      <path d="M18 21c0-10 28-10 28 0v2H18z" className={C.ink} />
      <rect x="16" y="22" width="32" height="4" rx="2" className={C.teal} />
      <circle cx="32" cy="16" r="2.5" className="pitch-icon-fill-mint" />
    </Frame>
  );
}

/** A document with lines. Used for a ledger or record. */
export function RecordIcon(props: PitchIconProps) {
  return (
    <Frame {...props}>
      <rect x="12" y="6" width="40" height="52" rx="4" className={C.teal} />
      <rect x="18" y="14" width="28" height="4" rx="2" className={C.soft} />
      <rect x="18" y="24" width="28" height="4" rx="2" className={C.soft} />
      <rect x="18" y="34" width="18" height="4" rx="2" className={C.soft} />
      <rect x="18" y="44" width="22" height="4" rx="2" className={C.accent} />
    </Frame>
  );
}

/** A desk with a counter sign. Used for a front-desk role. */
export function DeskIcon(props: PitchIconProps) {
  return (
    <Frame {...props}>
      <rect x="34" y="10" width="22" height="15" rx="2" className={C.accent} />
      <rect x="43" y="25" width="4" height="5" className={C.ink} />
      <rect x="4" y="30" width="56" height="6" rx="2" className={C.ink} />
      <rect x="7" y="36" width="50" height="22" rx="2" className={C.teal} />
      <rect x="13" y="43" width="20" height="4" rx="2" className={C.mint} />
    </Frame>
  );
}

type LucideLike = ComponentType<{
  size?: number | undefined;
  className?: string | undefined;
  "aria-hidden"?: boolean;
}>;

function wrapLucide(Icon: LucideLike) {
  return function Wrapped({ size = 32, className }: PitchIconProps) {
    return <Icon size={size} className={className} aria-hidden={true} />;
  };
}

export const CheckIcon = wrapLucide(CheckCircle2 as LucideLike);
export const TeamIcon = wrapLucide(Users as LucideLike);
export const BookIcon = wrapLucide(BookOpen as LucideLike);
export const DatabaseIcon = wrapLucide(Database as LucideLike);
export const SearchIcon = wrapLucide(Search as LucideLike);
export const ToolIcon = wrapLucide(Wrench as LucideLike);

/** Deck icon name to component. */
export const PITCH_ICONS = {
  planner: PlannerIcon,
  member: MemberIcon,
  chat: ChatIcon,
  coordinator: CoordinatorIcon,
  management: ManagementIcon,
  officer: OfficerIcon,
  supervisor: SupervisorIcon,
  record: RecordIcon,
  desk: DeskIcon,
  check: CheckIcon,
  team: TeamIcon,
  book: BookIcon,
  database: DatabaseIcon,
  search: SearchIcon,
  tool: ToolIcon,
} as const;

export type PitchIconName = keyof typeof PITCH_ICONS;

export function PitchIcon({ name, ...props }: PitchIconProps & { name: PitchIconName }) {
  const Icon = PITCH_ICONS[name];
  return <Icon {...props} />;
}
